import React from 'react';
import Typography from 'Components/Atoms/Typography';
import Button from 'Components/Atoms/Button';
import { ReservationShift } from 'types/shifts';
import {
  DayOverrideField,
  effectiveShiftSettings,
  hasDayOverride,
} from 'utils/dayOverrides';

type Props = {
  shift: Partial<ReservationShift>;
  series?: boolean;
  compact?: boolean;
  fields?: ('maxGuests' | 'slotsPerBlock' | 'eventV02')[];
  onReset?: (field: DayOverrideField) => void;
};

const DayCapacityDetails = ({
  shift,
  series = false,
  compact = false,
  fields,
  onReset,
}: Props) => {
  const effective = effectiveShiftSettings(shift, series);
  const rows = [
    {
      field: 'maxGuests' as const,
      label: 'Gäste insgesamt',
      value: effective.maxGuests ?? 'Ohne Grenze',
      base: shift.additional?.maxGuests ?? 'Ohne Grenze',
    },
    {
      field: 'slotsPerBlock' as const,
      label: 'Plätze pro Zeitblock',
      value: effective.slotsPerBlock?.standard ?? 'Standard',
      base: shift.additional?.slotsPerBlock?.standard ?? 'Standard',
    },
    {
      field: 'eventV02' as const,
      label: 'Tickets',
      value: effective.eventV02
        ? effective.eventV02.maxGuests ?? 'Ohne Grenze'
        : 'Kein Event',
      base: shift.additional?.eventV02
        ? shift.additional.eventV02.maxGuests ?? 'Ohne Grenze'
        : 'Kein Event',
    },
  ];
  return (
    <div style={{ margin: compact ? 0 : '12px 0' }}>
      <Typography block variant="text-3" weight={compact ? "medium" : "bold"}>
        {series
          ? 'Schichtwerte · Serie'
          : compact
          ? 'Buchungsgrenzen für diesen Tag'
          : 'Wirksame Grenzen · nur dieser Tag'}
      </Typography>
      {rows
        .filter((row) => !fields || fields.includes(row.field))
        .map((row) => (
          <div key={row.field} style={{ marginTop: 8 }}>
            <Typography block variant={compact ? "text-4" : "text-3"}>
              {row.label}: {row.value}
              {!series &&
                hasDayOverride(shift, row.field) &&
                ` · Tagesausnahme (Schichtwert: ${row.base})`}
            </Typography>
            {row.field === 'slotsPerBlock' && !!effective.slotsPerBlock?.rules?.length && (
              <Typography block variant="text-4" color="subdued">
                Zusätzliche Regeln pro Zeitblock gelten.
              </Typography>
            )}
            {!series && onReset && hasDayOverride(shift, row.field) && (
              <div>
                {row.field === 'eventV02' && (
                  <Typography block variant="text-4" color="subdued">
                    Die gesamte Event-Konfiguration wird zurückgesetzt. Bereits verkaufte
                    Tickets bleiben zugeordnet.
                  </Typography>
                )}
                <Button variant="secondary" onClick={() => onReset(row.field)}>
                  {row.field === 'eventV02'
                    ? 'Event-Tagesausnahme zurücksetzen'
                    : `${row.label} zurücksetzen`}
                </Button>
              </div>
            )}
          </div>
        ))}
      <Typography block variant="text-4" color="subdued" style={{ marginTop: 8 }}>
        {compact
          ? 'Buchungen sind nur möglich, solange alle Grenzen eingehalten werden.'
          : 'Jede Grenze gilt zusätzlich. Ein kleineres Kontingent kann Buchungen weiterhin begrenzen.'}
        {onReset &&
          ' Zurücksetzen übernimmt die normalen Schichtwerte; 0 Gesamtgäste schließen die Schicht. Änderungen werden mit Speichern übernommen.'}
      </Typography>
    </div>
  );
};
export default DayCapacityDetails;
